import React from 'react';
import { useSnapshot } from '../context/SnapshotContext';
import { SNAPSHOT_ENDPOINTS } from '../utils/offlineDB';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { useMutationCount } from '../hooks/useMutationCount';

const TOTAL = SNAPSHOT_ENDPOINTS.length;

// ── Formate l'âge du cache ────────────────────────────────────
function formatAge(ts) {
  if (!ts) return null;
  const diff = Date.now() - ts;
  const min  = Math.floor(diff / 60_000);
  const h    = Math.floor(min / 60);
  const d    = Math.floor(h / 24);
  if (d > 0)   return `${d}j`;
  if (h > 0)   return `${h}h`;
  if (min > 0) return `${min} min`;
  return '< 1 min';
}

// ── Icône spinner ────────────────────────────────────────────
function SpinnerIcon() {
  return (
    <svg className="snapshot-spin" width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  );
}

// ── Icône base de données ─────────────────────────────────────
function CacheIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
      <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
    </svg>
  );
}

// ── Icône sync ────────────────────────────────────────────────
function SyncIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9"/>
      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
      <polyline points="7 23 3 19 7 15"/>
      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Version MINI — sidebar repliée (icône 32×32 + badge/point)
// Clic : si mutations en attente → ouvre SyncPanel
//        sinon → rafraîchit le cache
// ─────────────────────────────────────────────────────────────
export function SnapshotButtonMini() {
  const { meta, isRefreshing, isSyncing, refresh, setShowSyncPanel } = useSnapshot();
  const isOffline    = useOfflineStatus();
  const pendingCount = useMutationCount();
  const busy         = isRefreshing || isSyncing;

  // Couleur du point de statut
  const dot = (isOffline || (meta && meta.status === 'error'))
    ? '#f87171'
    : pendingCount > 0  ? '#f59e0b'
    : !meta             ? '#5c6078'
    : meta.status === 'ok'      ? '#34d399'
    : meta.status === 'partial' ? '#fbbf24'
    : '#f87171';

  const pendingLabel = pendingCount > 0 ? ` · ${pendingCount} en attente` : '';
  const title = isOffline
    ? `Mode dégradé${pendingLabel}`
    : busy
      ? (isSyncing ? 'Synchronisation en cours…' : 'Mise à jour du cache…')
      : meta
        ? `Cache — il y a ${formatAge(meta.timestamp)}${pendingLabel}`
        : `Cache non disponible${pendingLabel}`;

  function handleClick() {
    if (busy) return;
    if (pendingCount > 0) setShowSyncPanel(true);
    else refresh();
  }

  return (
    <button
      className="footer-icon-btn snapshot-btn-mini"
      onClick={handleClick}
      disabled={busy && pendingCount === 0}
      title={title}
      style={{ position: 'relative' }}
    >
      {isSyncing ? <SpinnerIcon /> : isRefreshing ? <SpinnerIcon /> : pendingCount > 0 ? <SyncIcon /> : <CacheIcon />}

      {/* Badge numérique : modifications en attente */}
      {pendingCount > 0 && !busy ? (
        <span className="mutation-badge">
          {pendingCount > 9 ? '9+' : pendingCount}
        </span>
      ) : (
        <span style={{
          position: 'absolute', top: 3, right: 3,
          width: 6, height: 6, borderRadius: '50%',
          background: dot,
          border: '1px solid var(--bg-surface)',
          transition: 'background 0.3s',
        }} />
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Version COMPLÈTE — sidebar déroulée
// ─────────────────────────────────────────────────────────────
export function SnapshotButton() {
  const { meta, isRefreshing, isSyncing, progress, refresh, setShowSyncPanel } = useSnapshot();
  const isOffline    = useOfflineStatus();
  const pendingCount = useMutationCount();

  const pct  = isRefreshing ? Math.round((progress.current / TOTAL) * 100) : 0;
  const busy = isRefreshing || isSyncing;

  // Ligne de statut cache
  let statusLine  = null;
  let statusClass = '';
  if (isOffline) {
    statusLine  = '⚠ Serveur inaccessible';
    statusClass = 'snapshot-status--error';
  } else if (!isRefreshing && meta) {
    const age = formatAge(meta.timestamp);
    if (meta.status === 'ok')      { statusLine = `✓ Cache — il y a ${age}`; statusClass = 'snapshot-status--ok'; }
    else if (meta.status === 'partial') { statusLine = `⚠ Partiel — il y a ${age}`; statusClass = 'snapshot-status--partial'; }
    else                                { statusLine = `✕ Échec — il y a ${age}`; statusClass = 'snapshot-status--error'; }
  } else if (!meta && !isRefreshing) {
    statusLine = 'Cache non disponible'; statusClass = 'snapshot-status--partial';
  }

  return (
    <div className="snapshot-widget">

      {/* Ligne cache */}
      <button
        className={`btn-sidebar-action snapshot-btn${isRefreshing ? ' snapshot-btn--running' : ''}${isOffline ? ' snapshot-btn--disabled' : ''}`}
        onClick={() => !busy && refresh()}
        disabled={busy || isOffline}
        title="Rafraîchir le cache local"
      >
        <span className="snapshot-btn__icon">
          {isRefreshing ? <SpinnerIcon /> : <CacheIcon />}
        </span>
        <span className="snapshot-btn__text">
          {isRefreshing ? `${progress.label} (${pct}%)` : 'Cache local'}
        </span>
      </button>

      {isRefreshing && (
        <div className="snapshot-progress">
          <div className="snapshot-progress__fill" style={{ width: `${pct}%` }} />
        </div>
      )}

      {statusLine && !isRefreshing && (
        <div className={`snapshot-status ${statusClass}`}>{statusLine}</div>
      )}

      {/* Bouton Synchroniser (visible quand mutations en attente) */}
      {pendingCount > 0 && (
        <button
          className={`btn-sidebar-action snapshot-btn snapshot-btn--sync${isSyncing ? ' snapshot-btn--running' : ''}${isOffline ? ' snapshot-btn--disabled' : ''}`}
          onClick={() => !isSyncing && setShowSyncPanel(true)}
          disabled={isSyncing}
          title={isOffline ? 'Connexion requise' : 'Ouvrir le panneau de synchronisation'}
        >
          <span className="snapshot-btn__icon" style={{ color: isSyncing ? 'var(--accent-cyan)' : 'var(--accent-amber)' }}>
            {isSyncing ? <SpinnerIcon /> : <SyncIcon />}
          </span>
          <span className="snapshot-btn__text">
            {isSyncing ? 'Sync en cours…' : `Synchroniser (${pendingCount})`}
          </span>
        </button>
      )}
    </div>
  );
}
